import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { type Action } from '@ngrx/store';
import {
  catchError,
  concat,
  concatMap,
  defer,
  exhaustMap,
  filter,
  first,
  groupBy,
  map,
  mergeMap,
  type Observable,
  of,
  repeat,
  retry,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';

import {
  type ChatConversation,
  toOutgoingChatMessage,
  toPendingActionTarget,
} from '@blaze-dashboard/ai-chat-domain';
import { getErrorMessage } from '@blaze-shared/platform-data-access';

import { ChatContextRegistry } from '../chat-context.registry';
import { ChatService } from '../chat.service';

import * as ChatActionTargetActions from './chat-action-target.actions';
import * as ChatConversationActions from './chat-conversation.actions';
import * as ChatFeedbackActions from './chat-feedback.actions';
import * as ChatInterruptActions from './chat-interrupt.actions';
import * as ChatMessagesActions from './chat-messages.actions';
import * as ChatPanelActions from './chat-panel.actions';
import * as ChatPollingActions from './chat-polling.actions';
import * as ChatTemplateConfigurationSetupActions from './chat-template-configuration-setup.actions';
import { ChatFacade } from './chat.facade';
import { getNewClientId, POLLING_INTERVAL } from './chat.models';

const NOT_READY_TO_SEND_ERROR = "The chat isn't ready yet. Please try again in a moment.";

const MAX_CONSECUTIVE_POLL_FAILURES = 5;

const POLL_PAGE_SIZE = 30;

const BOOTSTRAP_PAGE_SIZE = 1000;

function toStartPollingChatMessages(
  conversation: ChatConversation,
): ReturnType<typeof ChatPollingActions.startPollingChatMessages> {
  return ChatPollingActions.startPollingChatMessages({
    chatConversationId: conversation.id,
    projectId: conversation.projectId,
  });
}

function pollMessagesAfter(
  chatService: ChatService,
  chatConversationId: string,
  afterMessageId: string,
): Observable<Action> {
  return chatService
    .loadMessages(chatConversationId, { take: POLL_PAGE_SIZE, afterMessageId })
    .pipe(
      map((chatMessagesResponse) =>
        ChatPollingActions.chatMessagesPolled({ chatMessagesResponse }),
      ),
    );
}

function loadMessagesFromTheTop(
  chatService: ChatService,
  chatConversationId: string,
  options: { take?: number },
): Observable<Action> {
  return chatService
    .loadMessages(chatConversationId, options)
    .pipe(
      map((chatMessagesResponse) =>
        ChatMessagesActions.loadChatMessagesSuccess({ chatMessagesResponse }),
      ),
    );
}

function isUnknownAnchorRejection(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === HttpStatusCode.BadRequest;
}

function pollMessagesContinuously(
  chatService: ChatService,
  chatFacade: ChatFacade,
  chatConversationId: string,
): Observable<Action> {
  return defer(() => {
    let recoveredAnchor: string | undefined;
    let hasBootstrapped = false;

    return chatFacade.lastPersistedMessageId$.pipe(
      take(1),
      switchMap((anchor) => {
        if (!anchor) {
          return loadMessagesFromTheTop(chatService, chatConversationId, {
            take: hasBootstrapped ? POLL_PAGE_SIZE : BOOTSTRAP_PAGE_SIZE,
          }).pipe(tap(() => (hasBootstrapped = true)));
        }

        return pollMessagesAfter(chatService, chatConversationId, anchor).pipe(
          catchError((error: unknown) => {
            if (anchor === recoveredAnchor || !isUnknownAnchorRejection(error)) {
              return throwError(() => error);
            }

            recoveredAnchor = anchor;
            return loadMessagesFromTheTop(chatService, chatConversationId, {});
          }),
        );
      }),
      repeat({ delay: POLLING_INTERVAL }),
      retry({
        count: MAX_CONSECUTIVE_POLL_FAILURES - 1,
        delay: POLLING_INTERVAL,
        resetOnSuccess: true,
      }),
    );
  });
}

export const chatOpened$ = createEffect(
  (actions$ = inject(Actions), chatFacade = inject(ChatFacade)) => {
    return actions$.pipe(
      ofType(ChatPanelActions.chatPanelOpened),
      concatLatestFrom(() => chatFacade.coveredProjectId$),
      filter(([{ projectId }, coveredProjectId]) => projectId !== coveredProjectId),
      map(([{ projectId }]) => ChatConversationActions.loadLatestChatConversation({ projectId })),
    );
  },
  { functional: true },
);

export const loadLatestConversation$ = createEffect(
  (actions$ = inject(Actions), chatService = inject(ChatService)) => {
    return actions$.pipe(
      ofType(ChatConversationActions.loadLatestChatConversation),
      switchMap(({ projectId }) =>
        chatService.loadLatestChatConversation(projectId).pipe(
          map((conversation) =>
            conversation
              ? ChatConversationActions.loadLatestChatConversationSuccess({ conversation })
              : ChatConversationActions.createChatConversation({ projectId }),
          ),
          catchError((error) =>
            of(
              ChatConversationActions.loadLatestChatConversationFailure({
                error: getErrorMessage(error, "We couldn't load the chat. Please try again later."),
                projectId,
              }),
            ),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const createChatConversation$ = createEffect(
  (
    actions$ = inject(Actions),
    chatService = inject(ChatService),
    chatFacade = inject(ChatFacade),
  ) => {
    return actions$.pipe(
      ofType(ChatConversationActions.createChatConversation),
      groupBy(({ projectId }) => projectId),
      mergeMap((createsForOneProject$) =>
        createsForOneProject$.pipe(
          exhaustMap(({ projectId }) =>
            chatService.createChatConversation(projectId).pipe(
              map((conversation) =>
                ChatConversationActions.createChatConversationSuccess({ conversation }),
              ),
              catchError((error) =>
                of(
                  ChatConversationActions.createChatConversationFailure({
                    error: getErrorMessage(
                      error,
                      "We couldn't load the chat. Please try again later.",
                    ),
                    projectId,
                  }),
                ),
              ),
              concatLatestFrom(() => chatFacade.panelProjectId$),
              filter(([, panelProjectId]) => panelProjectId === projectId),
              map(([outcome]) => outcome),
            ),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const sendMessage$ = createEffect(
  (
    actions$ = inject(Actions),
    chatService = inject(ChatService),
    chatFacade = inject(ChatFacade),
    contextRegistry = inject(ChatContextRegistry),
  ) => {
    return actions$.pipe(
      ofType(ChatMessagesActions.sendChatMessage),
      concatLatestFrom(() => [
        chatFacade.currentConversation$,
        chatFacade.hasLoadedMessages$,
        chatFacade.lastPersistedMessageId$,
        contextRegistry.currentContext$,
      ]),
      concatMap(
        ([
          { clientId, message: request },
          conversation,
          hasLoadedMessages,
          lastMessageId,
          ambient,
        ]) => {
          if (!conversation || !hasLoadedMessages) {
            return of(
              ChatMessagesActions.sendChatMessageFailure({
                clientId,
                message: request,
                error: NOT_READY_TO_SEND_ERROR,
              }),
            );
          }

          const context = request.context ?? ambient;
          const apiRequest = toOutgoingChatMessage(request, {
            context,
            previousMessageId: lastMessageId,
          });
          const send$ = chatService.sendMessage(conversation.id, apiRequest).pipe(
            map((message) =>
              ChatMessagesActions.sendChatMessageSuccess({
                chatConversationId: conversation.id,
                clientId,
                message,
              }),
            ),
            catchError((error) =>
              of(
                ChatMessagesActions.sendChatMessageFailure({
                  chatConversationId: conversation.id,
                  clientId,
                  error: getErrorMessage(
                    error,
                    "We couldn't send your message. Please try again later.",
                  ),
                  message: request,
                }),
              ),
            ),
          );
          const target = toPendingActionTarget(context);
          return target
            ? concat(of(ChatActionTargetActions.pendingActionTargetRecorded({ target })), send$)
            : send$;
        },
      ),
    );
  },
  { functional: true },
);

export const startTemplateConfigurationSetup$ = createEffect(
  (
    actions$ = inject(Actions),
    chatService = inject(ChatService),
    chatFacade = inject(ChatFacade),
  ) => {
    return actions$.pipe(
      ofType(ChatTemplateConfigurationSetupActions.startTemplateConfigurationSetup),
      concatLatestFrom(() => [chatFacade.currentConversation$, chatFacade.lastPersistedMessageId$]),
      concatMap(([{ name, params }, conversation, previousMessageId]) => {
        if (!conversation) {
          return of(
            ChatTemplateConfigurationSetupActions.startTemplateConfigurationSetupFailure({
              name,
              error: NOT_READY_TO_SEND_ERROR,
            }),
          );
        }

        return chatService
          .sendMessage(conversation.id, {
            $type: 'start_wizard',
            wizardName: name,
            params,
            ...(previousMessageId && { previousMessageId }),
          })
          .pipe(
            map(() =>
              ChatTemplateConfigurationSetupActions.startTemplateConfigurationSetupSuccess(),
            ),
            catchError((error) =>
              of(
                ChatTemplateConfigurationSetupActions.startTemplateConfigurationSetupFailure({
                  chatConversationId: conversation.id,
                  name,
                  error: getErrorMessage(error, "We couldn't start setting up this element."),
                }),
              ),
            ),
          );
      }),
    );
  },
  { functional: true },
);

export const exitTemplateConfigurationSetup$ = createEffect(
  (
    actions$ = inject(Actions),
    chatService = inject(ChatService),
    chatFacade = inject(ChatFacade),
  ) => {
    return actions$.pipe(
      ofType(ChatTemplateConfigurationSetupActions.exitTemplateConfigurationSetup),
      concatLatestFrom(() => [chatFacade.currentConversation$, chatFacade.lastPersistedMessageId$]),
      concatMap(([, conversation, previousMessageId]) => {
        if (!conversation) {
          return of(
            ChatTemplateConfigurationSetupActions.exitTemplateConfigurationSetupFailure({
              error: NOT_READY_TO_SEND_ERROR,
            }),
          );
        }

        return chatService
          .sendMessage(conversation.id, {
            $type: 'exit_wizard',
            ...(previousMessageId && { previousMessageId }),
          })
          .pipe(
            map(() =>
              ChatTemplateConfigurationSetupActions.exitTemplateConfigurationSetupSuccess(),
            ),
            catchError((error) =>
              of(
                ChatTemplateConfigurationSetupActions.exitTemplateConfigurationSetupFailure({
                  error: getErrorMessage(error, "We couldn't stop setting up this element."),
                }),
              ),
            ),
          );
      }),
    );
  },
  { functional: true },
);

export const sendFeedback$ = createEffect(
  (
    actions$ = inject(Actions),
    chatService = inject(ChatService),
    chatFacade = inject(ChatFacade),
  ) => {
    return actions$.pipe(
      ofType(ChatFeedbackActions.sendFeedback),
      concatLatestFrom(() => chatFacade.currentConversation$),
      concatMap(([{ feedback, messageId }, conversation]) => {
        if (!conversation) {
          return of(
            ChatFeedbackActions.sendFeedbackFailure({
              feedback,
              messageId,
              error: NOT_READY_TO_SEND_ERROR,
            }),
          );
        }

        return chatService.sendFeedback(conversation.id, messageId, feedback).pipe(
          map(() => ChatFeedbackActions.sendFeedbackSuccess({ feedback, messageId })),
          catchError((error) =>
            of(
              ChatFeedbackActions.sendFeedbackFailure({
                feedback,
                messageId,
                error: getErrorMessage(
                  error,
                  "We couldn't send your feedback. Please try again later.",
                ),
              }),
            ),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const interruptChat$ = createEffect(
  (
    actions$ = inject(Actions),
    chatService = inject(ChatService),
    chatFacade = inject(ChatFacade),
  ) => {
    return actions$.pipe(
      ofType(ChatInterruptActions.interruptChat),
      concatLatestFrom(() => chatFacade.currentConversation$),
      concatMap(([, conversation]) => {
        if (!conversation) {
          return of(ChatInterruptActions.interruptChatFailure({ error: NOT_READY_TO_SEND_ERROR }));
        }

        return chatService.interruptChat(conversation.id).pipe(
          map(() =>
            ChatInterruptActions.interruptChatSuccess({ chatConversationId: conversation.id }),
          ),
          catchError((error) =>
            of(
              ChatInterruptActions.interruptChatFailure({
                chatConversationId: conversation.id,
                error: getErrorMessage(error, "We couldn't stop the chat. Please try again later."),
              }),
            ),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const retrySendChatMessage$ = createEffect(
  (actions$ = inject(Actions), chatFacade = inject(ChatFacade)) => {
    return actions$.pipe(
      ofType(ChatMessagesActions.retrySendChatMessage),
      concatLatestFrom(() => chatFacade.lastFailedMessage$.pipe(filter(Boolean), first())),
      map(([, message]) =>
        ChatMessagesActions.sendChatMessage({ clientId: getNewClientId(), message }),
      ),
    );
  },
  { functional: true },
);

export const startPollingChatMessages$ = createEffect(
  (actions$ = inject(Actions), chatFacade = inject(ChatFacade)) => {
    return actions$.pipe(
      ofType(
        ChatConversationActions.loadLatestChatConversationSuccess,
        ChatConversationActions.createChatConversationSuccess,
      ),
      map(({ conversation }) => conversation),
      concatLatestFrom(() => chatFacade.livePollingConversationId$),
      filter(
        ([conversation, livePollingConversationId]) =>
          conversation.id !== livePollingConversationId,
      ),
      map(([conversation]) => toStartPollingChatMessages(conversation)),
    );
  },
  { functional: true },
);

export const retryPollingChatMessages$ = createEffect(
  (actions$ = inject(Actions), chatFacade = inject(ChatFacade)) => {
    return actions$.pipe(
      ofType(ChatPollingActions.retryPollingChatMessages),
      concatLatestFrom(() => chatFacade.currentConversation$.pipe(filter(Boolean), first())),
      map(([, conversation]) => toStartPollingChatMessages(conversation)),
    );
  },
  { functional: true },
);

export const pollChatMessages$ = createEffect(
  (
    actions$ = inject(Actions),
    chatService = inject(ChatService),
    chatFacade = inject(ChatFacade),
  ) => {
    return actions$.pipe(
      ofType(ChatPollingActions.startPollingChatMessages),
      switchMap(({ chatConversationId }) =>
        pollMessagesContinuously(chatService, chatFacade, chatConversationId).pipe(
          catchError((error) =>
            of(ChatPollingActions.chatMessagesPollingFailure({ error: getErrorMessage(error) })),
          ),
        ),
      ),
    );
  },
  { functional: true },
);
